/* eslint-disable import/no-deprecated */
import { classNames } from '@fullstory/util/fmt';
import { every } from 'lodash';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Popover } from '../../components/Popover';
import { TextInput } from '../../components/TextInput';
import { Box } from '../../legacy/Box';
import { Flex } from '../../legacy/Flex';
import { Pane } from '../../legacy/Pane';
import { listNav } from '../ListNav';
import {
  DeprecatedBaseOptionMenuProps,
  DeprecatedOptionMenu,
  DeprecatedOptionMenuCategoryProps,
} from '../OptionMenu';
import { DeprecatedSelect } from '../Select';
import { Typography } from '../Typography';

type SearchCategoryIntent = 'searchCategoryDependent' | 'searchCategoryPrimary';

/**
 * @deprecated
 */
export type AutocompleteCategoryOps<T> = {
  eqCategory: (a: T, B: T) => boolean;
  onCategoryChange: (category: string) => void;
  mapCategoryToHint: (category: string) => string;
  category: string;
  categoryOptions: string[];
  hideCategorySelector: boolean;
  intent: SearchCategoryIntent;
} & DeprecatedOptionMenuCategoryProps<T>;

interface AutoCompleteProps<T>
  extends DeprecatedBaseOptionMenuProps<T>,
    Pick<React.ComponentProps<typeof TextInput>, 'variant' | 'size' | 'onBlur'> {
  /** Optional classname to add onto each item in the autocomplete list */
  optionMenuItemClassName?: string;
  /**
   * The current selected value (if any). When a value is set, and the
   * autocomplete is not in a dirty state, the Autocomplete will display as the return value
   * from `optionText(value)`.
   */
  value: T | null;
  /** Placeholder for the AutoComplete element. */
  placeholder: string;
  /**
   * Callback for when the filter text changes.
   * This is a good place to do things like fetching more options from a server.
   * This will only be called when a user types or removes a character from the
   * TextInput.
   *
   * If the text is an empty string, upstream services should reset the `value` prop to
   * null, otherwise text from the autocomplete cannot ever be completely cleared.
   */
  onFilterChange: (text: string) => Promise<void>;
  /**
   * If true, filter changes will be sent as soon as the input is focused, even if there
   * are no values, or if a default value is provided.
   */
  sendEmptyFilter?: boolean;
  /**
   * className that applies to the text input component.
   */
  textInputClassName?: string;
  // Optionally show title attribute over the autocompletion options.
  showOptionTitles?: boolean;
  /**
   * onExit callback is triggered when the user exits the component, after onBlur and onSelect
   *
   * onBlur and onSelect both trigger on component exit; onExit used for behavior that only needs to be
   * triggered once on close
   */
  onExit?: () => void;

  categoryOps?: AutocompleteCategoryOps<T>;

  // event handler to react to cut event. NOTE: onChange is also called after onCut.
  onCut?: React.ClipboardEventHandler<HTMLInputElement>;
  // event handler to react to paste event. NOTE: onChange is also called after onPaste.
  onPaste?: React.ClipboardEventHandler<HTMLInputElement>;
  // event handler to react to a focus event. filter change will still be called.
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  // event handler to react to a focus event. filter change will still be called.
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
}

interface AutoCompleteState {
  open: boolean;
  /**
   * If true, the Autocomplete has received new input, but a user has not chosen
   * an entry.
   */
  dirty: boolean;
  /**
   * The user-input text, set when the Autocomplete is 'dirty'
   */
  filterText: string;
  /**
   * If the user is using the arrow keys to navigate, this represents the optionID of the current
   * selected item.
   */
  focusedOptionId: string | null;
  /**
   * Set to true before the user has searched for anything, when true, the no results filter will not show up.
   */
  firstSearch: boolean;
  error: Error | null;
}

type CategoryPrefixSelectorProps<C> = {
  onSelect: (category: string) => void;
  categoryOptions: C[];
  category: C;
  mapCategoryToHint: (cat: C) => string;
  lockCategory: boolean;
  categoryToDisplay: (cat: string) => string;
  intent: SearchCategoryIntent;
};

function CategorySelector<C extends string>(props: CategoryPrefixSelectorProps<C>) {
  return (
    <Box data-component="CategorySelector" className="valueMetaSelectContainer">
      <DeprecatedSelect<C>
        className="valueMetaSelect"
        options={props.categoryOptions}
        intent={props.intent}
        value={props.category}
        optionId={v => v}
        optionText={v => props.categoryToDisplay(v)}
        onSelect={category => {
          props.onSelect(category);
        }}
        size="x-small"
        variant={props.intent == 'searchCategoryDependent' ? 'minimal' : 'outline'}
        hideSearch
        locked={props.lockCategory}
        renderBelow={() => (
          <Pane padding={12} borderTop="standard">
            <Typography variant="body3" color="neutrals-n7" fontWeight="regular">
              <FormattedMessage
                defaultMessage="Note: Multivalue URL searches must be of the same type."
                id="fw2WD8"
              />
            </Typography>
          </Pane>
        )}
        renderItem={(option: C) => (
          <Flex justifyContent="space-between">
            <Box style={{ minWidth: '120px' }}>
              <Typography variant="heading6" fontWeight="medium" color="pink">
                {option}
              </Typography>
            </Box>
            <Box flexGrow={1} paddingLeft={4}>
              <Typography variant="body3" color="neutrals-n7" fontWeight="regular">
                {props.mapCategoryToHint(option)}
              </Typography>
            </Box>
          </Flex>
        )}
      />
    </Box>
  );
}

/**
 * @deprecated
 */
class AutoComplete<T> extends React.PureComponent<AutoCompleteProps<T>, AutoCompleteState> {
  static readonly defaultProps = {
    size: 'small',
  };

  state: AutoCompleteState = {
    open: false,
    dirty: false,
    filterText: '',
    focusedOptionId: null,
    firstSearch: true,
    error: null,
  };

  private readonly freeformId = 'freeform';

  /**
   *  When the option menu is being clicked, prevent it from hiding. Without this, the blur event
   *  will be fired during the click (on mousedown) - causing the option menu to hide,
   *  but the onSelect method doesn't fire until the click event completes (on mouseup)
   */
  closePrevented: boolean = false;

  private readonly inputRef = React.createRef<HTMLInputElement>();

  navItems: { opt: T; id: string }[];

  // whether or not the component is mounted
  // used to avoid setState for async functions / callbacks
  private _mounted = false;

  get focusedOptionId(): string | null {
    if (!this.state || !this.state.focusedOptionId) {
      return null;
    }
    return this.state.focusedOptionId;
  }

  constructor(props: AutoCompleteProps<T>) {
    super(props);
    this.navItems = withIds(this.props.options, this.props.optionId);
  }

  componentDidUpdate() {
    const optionsWithIds = withIds(this.props.options, this.props.optionId);
    if (
      !(
        this.navItems.length === optionsWithIds.length &&
        every(this.navItems, (item, i) => item.id === optionsWithIds[i]?.id)
      )
    ) {
      this.navItems = optionsWithIds;
    }
  }

  componentDidMount() {
    this._mounted = true;
  }

  componentWillUnmount() {
    this._mounted = false;
  }

  private showMainOptionMenu(): boolean {
    return !this.state.error && this.props.options && !!this.props.options.length;
  }

  private showNoResults(): boolean {
    return (
      !this.state.error &&
      !this.state.firstSearch &&
      (!this.props.options || !this.props.options.length)
    );
  }

  private value(): string {
    return this.state.dirty
      ? this.state.filterText
      : this.props.value
      ? this.props.optionText(this.props.value)
      : this.state.filterText;
  }

  render() {
    // NOTE(ryan): if we ever need categorical text inputs standalone from Autocompletion,
    // create a wrapper component over TextInputComponent that extracts this away from Autocomplete.
    const CategoryPrefixComponent = () => {
      const { categoryOps } = this.props;

      if (!categoryOps || (categoryOps && categoryOps.hideCategorySelector)) {
        return null;
      }
      return (
        <CategorySelector
          {...categoryOps}
          categoryToDisplay={(cat: string) => categoryOps.categoryToDisplay(cat)}
          onSelect={category => {
            categoryOps.onCategoryChange(category);
          }}
        />
      );
    };

    const value = this.value();

    return (
      <>
        <Flex>
          <CategoryPrefixComponent />
          <TextInput
            ref={this.inputRef}
            type="text"
            className={this.props.textInputClassName}
            value={value}
            placeholder={this.props.placeholder}
            onChange={this.handleFilterChange}
            onCut={this.props.onCut}
            onPaste={this.props.onPaste}
            onFocus={this.handleFocus}
            onBlur={e => {
              // If we have a menu portal open and the user selects an entry, ignore the blur that is also accompanied with the portal close signal dispatching.
              if (!this.showNoResults() && !this.showMainOptionMenu()) {
                this.handleClose();
              }
              if (this.props.onBlur) this.props.onBlur(e);
            }}
            onKeyDown={this.showNoResults() ? this.handleFreeformKeyDown : this.handleKeyDown}
            variant={this.props.variant}
            size={this.props.size}
          />
        </Flex>
        {this.state.open && (
          <>
            {
              // Autocomplete has errored, show the dialogue
              this.state.error && this.inputRef.current && (
                <Popover
                  open
                  anchor={this.inputRef.current}
                  onCloseSignal={this.handleClose}
                  offset={6}
                  overflow="hidden"
                >
                  <FormattedMessage defaultMessage="Unable to fetch options." id="j9//2f" />
                </Popover>
              )
            }
            {
              // Autocomplete has not errored, and has real values
              this.showMainOptionMenu() && this.inputRef.current && (
                /* eslint-disable jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */
                <div onMouseDown={this.preventClose} onMouseUp={this.allowClose}>
                  <DeprecatedOptionMenu
                    {...this.props}
                    itemClassName={classNames('optionMenuItem', this.props.optionMenuItemClassName)}
                    className={classNames('autoComplete', this.props.className)}
                    anchor={this.inputRef.current}
                    options={this.props.options}
                    optionId={this.props.optionId}
                    optionText={this.props.optionText}
                    onSelect={this.handleSelect}
                    onCloseSignal={this.handleClose}
                    focusedOptionId={this.state.focusedOptionId}
                    maxHeight={this.props.maxHeight}
                    showTitles
                  />
                </div>
              )
            }
            {
              // Autocomplete has not errored, but has no values
              this.showNoResults() &&
                this.inputRef.current &&
                (this.props.categoryOps && value && value.length > 0 ? (
                  <DeprecatedOptionMenu<string>
                    itemClassName={classNames('optionMenuItem', this.props.optionMenuItemClassName)}
                    className={classNames('autoComplete', this.props.className)}
                    anchor={this.inputRef.current}
                    options={[value]}
                    optionId={val => 'freeform'}
                    optionText={() => value}
                    onSelect={this.handleFreeform}
                    onCloseSignal={this.handleClose}
                    focusedOptionId={this.state.focusedOptionId}
                    maxHeight={this.props.maxHeight}
                    showTitles
                    categoryOps={{
                      categoryFromOption: () => '',
                      categoryToDisplay: () => this.freeformId,
                      eqCategory: () => true, // there's only 1 option.
                      lockCategory: false,
                    }}
                  />
                ) : (
                  <Popover
                    open
                    anchor={this.inputRef.current}
                    onCloseSignal={this.handleClose}
                    offset={6}
                    overflow="hidden"
                  >
                    <FormattedMessage defaultMessage="No results." id="0H2hdf" />
                  </Popover>
                ))
            }
          </>
        )}
      </>
    );
  }

  private handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (this.props.onFocus) {
      this.props.onFocus(e);
    }
    this.setState({
      open: true,
    });
    if (this.props.sendEmptyFilter) {
      this.sendFilterText(this.props.value ? this.props.optionText(this.props.value) : '');
    }
  };

  private handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const filterText = e.target.value;
    this.setState({
      filterText,
      dirty: true,
    });
    this.sendFilterText(filterText);
  };

  private sendFilterText(filterText: string) {
    this.props
      .onFilterChange(filterText)
      .then(() => {
        if (this._mounted) {
          this.setState({ error: null, firstSearch: false });
        }
      })
      .catch(error => {
        if (this._mounted) {
          this.setState({ error, firstSearch: false });
        }
      });
  }

  private handleSelect = (opt: T) => {
    this.props.onSelect(opt);
    this.handleClose();
  };

  // freeform as an option menu item is an illusion; it exactly mirrors the text input so when you click it
  // all we need to do is close the popup, the current value will already be propagated up per the onchange.
  private handleFreeform = () => {
    this.handleClose();
  };

  private preventClose = () => {
    this.closePrevented = true;
  };

  private allowClose = () => {
    this.closePrevented = false;
  };

  private handleClose = () => {
    if (this.closePrevented) return;
    this.setState({
      open: false,
      dirty: false,
      focusedOptionId: null,
      filterText: '',
      error: null,
      firstSearch: true,
    });
    this.blur();
    if (this.props.onExit) this.props.onExit();
  };

  private blur() {
    if (this.inputRef && this.inputRef.current) {
      this.inputRef.current.blur();
    }
  }

  focus() {
    if (this.inputRef && this.inputRef.current) {
      this.inputRef.current.focus();
    }
  }

  private handleFreeformKeyDown = listNav(
    {
      navItems: [{ opt: this.value(), id: this.freeformId }],
      focusedOptionId: this.state.focusedOptionId,
    },
    {
      arrowKey: id => {
        this.setState({ focusedOptionId: id });
      },
      enter: () => {
        this.handleFreeform();
      },
    },
  );

  private handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (this.props.onKeyDown) {
      this.props.onKeyDown(e);
    }
    return listNav(this, {
      arrowKey: id => {
        this.setState({ focusedOptionId: id });
      },
      enter: id => {
        const opt = this.props.options.find((opt, i) => this.props.optionId(opt, i) == id);
        if (opt) {
          this.handleSelect(opt);
        } else {
          this.handleClose();
        }
      },
    })(e);
  };
}

function withIds<T>(opts: T[], idFor: (opt: T, index: number) => string) {
  return opts.map((opt, i) => ({ opt, id: idFor(opt, i) }));
}

export { AutoComplete };
